import { create } from 'zustand';
import { auth as authApi, isMfaChallenge } from '@/api/endpoints';
import { registerUnauthenticatedHandler, refreshSession, resetClientState, setAccessToken } from '@/api/client';
import type { CurrentUser, Role } from '@/api/types';

type Status = 'unknown' | 'authenticated' | 'anonymous';

interface AuthState {
  status: Status;
  user: CurrentUser | null;
  /** Set when the password was accepted but a second factor is outstanding. */
  mfaToken: string | null;
  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<'ok' | 'mfa'>;
  completeMfa: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  clear: () => void;
  can: (permission: string) => boolean;
  hasRole: (...roles: Role[]) => boolean;
}

export const useAuth = create<AuthState>((set, get) => ({
  status: 'unknown',
  user: null,
  mfaToken: null,

  /**
   * Restore a session on page load.
   *
   * The access token is in memory only, so a refresh of the browser loses it.
   * The refresh cookie survives, so the app asks for a new access token before
   * deciding the user is logged out. Without this, every reload would bounce
   * the operator back to the login screen.
   */
  async bootstrap() {
    const refreshed = await refreshSession();
    if (!refreshed) {
      set({ status: 'anonymous', user: null });
      return;
    }
    try {
      set({ user: await authApi.me(), status: 'authenticated' });
    } catch {
      set({ status: 'anonymous', user: null });
    }
  },

  async login(email, password) {
    const result = await authApi.login(email, password);
    if (isMfaChallenge(result)) {
      set({ mfaToken: result.mfa_token });
      return 'mfa';
    }
    set({ user: await authApi.me(), status: 'authenticated', mfaToken: null });
    return 'ok';
  },

  async completeMfa(code) {
    const token = get().mfaToken;
    if (!token) throw new Error('Start again from the sign-in screen.');
    await authApi.completeMfa(token, code);
    set({ user: await authApi.me(), status: 'authenticated', mfaToken: null });
  },

  async logout() {
    await authApi.logout();
    resetClientState();
    set({ status: 'anonymous', user: null, mfaToken: null });
  },

  clear() {
    setAccessToken(null);
    set({ status: 'anonymous', user: null, mfaToken: null });
  },

  /**
   * Permission checks drive what the interface offers.
   *
   * This is presentation only. The server enforces the same matrix and is the
   * actual control; hiding a button the API would refuse just avoids offering
   * people actions that will fail.
   */
  can(permission) {
    return get().user?.permissions.includes(permission) ?? false;
  },

  hasRole(...roles) {
    const role = get().user?.role;
    return role ? roles.includes(role) : false;
  },
}));

registerUnauthenticatedHandler(() => useAuth.getState().clear());
